import { useEffect, useState } from "react";
import { Download, Share, Plus, X, Smartphone, Check } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  canPromptInstall,
  detectPlatform,
  isStandalone,
  promptInstall,
  subscribeInstallState,
  type Platform,
} from "@/lib/pwa";

const DISMISSED_KEY = "md_install_dismissed";

/** Stato di installabilità, condiviso tra banner e scheda in Impostazioni. */
function useInstallState() {
  const [platform] = useState<Platform>(() => detectPlatform());
  const [installed, setInstalled] = useState(() => isStandalone());
  const [promptReady, setPromptReady] = useState(() => canPromptInstall());

  useEffect(() => {
    const unsub = subscribeInstallState(() => {
      setPromptReady(canPromptInstall());
      setInstalled(isStandalone());
    });
    // Chrome può emettere `beforeinstallprompt` dopo il primo render
    const t = window.setTimeout(() => setPromptReady(canPromptInstall()), 1500);
    return () => {
      unsub();
      window.clearTimeout(t);
    };
  }, []);

  return { platform, installed, promptReady };
}

async function runInstall() {
  const outcome = await promptInstall();
  if (outcome === "accepted") toast.success("Machiavelli Desk installato");
  else if (outcome === "unavailable") {
    toast.info("Usa il menu del browser → «Installa app»");
  }
}

/**
 * Banner una tantum sotto la barra in basso: compare solo su telefono, solo
 * se l'app non è già installata, e una volta chiuso non torna.
 */
export function InstallBanner() {
  const { platform, installed, promptReady } = useInstallState();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === "1",
  );

  const isPhone = platform === "ios" || platform === "android";
  // Su Android aspettiamo l'evento di Chrome; su iOS non esiste, quindi
  // mostriamo comunque le istruzioni.
  const show =
    isPhone && !installed && !dismissed && (platform === "ios" || promptReady);

  function close() {
    localStorage.setItem(DISMISSED_KEY, "1");
    setDismissed(true);
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          className="bottom-safe fixed inset-x-3 z-30 rounded-[var(--radius)] border border-primary/25 bg-card-2/95 p-3 shadow-renaissance backdrop-blur-xl md:hidden"
          role="complementary"
          aria-label="Installa l'app"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-[var(--radius)] bg-gradient-brand opacity-70"
          />
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <Smartphone className="h-5 w-5 text-primary" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-sm font-semibold leading-tight">
                Tieni il Desk sulla home
              </p>
              {platform === "ios" ? (
                <p className="mt-1 text-xs leading-snug text-muted-foreground">
                  Tocca{" "}
                  <Share className="inline h-3.5 w-3.5 -translate-y-px text-primary" />{" "}
                  <span className="text-foreground">Condividi</span>, poi{" "}
                  <Plus className="inline h-3.5 w-3.5 -translate-y-px text-primary" />{" "}
                  <span className="text-foreground">Aggiungi a Home</span>.
                </p>
              ) : (
                <p className="mt-1 text-xs leading-snug text-muted-foreground">
                  Si apre a schermo intero, senza barre del browser.
                </p>
              )}
              {platform === "android" && (
                <Button
                  size="sm"
                  variant="soft"
                  className="mt-2"
                  onClick={() => {
                    void runInstall();
                    close();
                  }}
                >
                  <Download className="h-3.5 w-3.5" />
                  Installa
                </Button>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="-mr-1 -mt-1 shrink-0"
              onClick={close}
              aria-label="Non mostrare più"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Blocco per Impostazioni: sempre raggiungibile, anche dopo aver chiuso il banner. */
export function InstallAppSection({ className }: { className?: string }) {
  const { platform, installed, promptReady } = useInstallState();

  if (installed) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 rounded-[var(--radius)] border border-success/25 bg-success/5 p-3",
          className,
        )}
      >
        <Check className="h-4 w-4 shrink-0 text-success" />
        <p className="text-sm text-muted-foreground">
          Stai già usando Machiavelli Desk come app installata.
        </p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {platform === "ios" ? (
        <ol className="space-y-2 text-sm text-muted-foreground">
          <Step n={1}>
            Apri questa pagina in <span className="text-foreground">Safari</span>.
          </Step>
          <Step n={2}>
            Tocca{" "}
            <Share className="inline h-4 w-4 -translate-y-px text-primary" />{" "}
            <span className="text-foreground">Condividi</span> nella barra in
            basso.
          </Step>
          <Step n={3}>
            Scegli{" "}
            <Plus className="inline h-4 w-4 -translate-y-px text-primary" />{" "}
            <span className="text-foreground">Aggiungi a Home</span>, poi{" "}
            <span className="text-foreground">Aggiungi</span>.
          </Step>
        </ol>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Machiavelli Desk si installa come applicazione: icona sulla home,
            schermo intero, nessuna barra del browser.
          </p>
          <Button variant="soft" onClick={() => void runInstall()}>
            <Download className="h-4 w-4" />
            {promptReady ? "Installa l'app" : "Installa (dal menu del browser)"}
          </Button>
        </>
      )}
    </div>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 font-display text-xs font-semibold text-primary">
        {n}
      </span>
      <span className="pt-0.5 leading-snug">{children}</span>
    </li>
  );
}
